import AttrValue, { AttrValueOptions } from 'models/AttrValue';
import EpisodeGroup, { EpisodeGroupOptions } from 'models/EpisodeGroup';
import LocationEpisodeDischarge, { LocationEpisodeDischargeOptions } from 'models/LocationEpisodeDischarge';
import LocationEpisodeSummary, { LocationEpisodeSummaryOptions } from 'models/LocationEpisodeSummary';
import User, { UserOptions } from 'models/User';

import Group, { GroupOptions } from './Group';
import LocationEpisode, { LocationEpisodeOptions } from './LocationEpisode';
import Patient, { PatientOptions } from './Patient';
import RehabState, { RehabStateOptions, RehabStates } from './RehabState';

export enum EpisodeAccessLevel {
  RESTRICTED = 'restricted',
  VIEW_ONLY = 'view_only',
  FULL = 'full',
}

export interface EpisodeOptions {
  id: string;
  payor: string | null;
  medicare: boolean | null;
  admittedOn: string | Date;
  currentRehabState: Partial<RehabStateOptions>;
  patient: Partial<PatientOptions>;
  physicianTeam: Partial<AttrValueOptions>;
  affiliates: Partial<GroupOptions>[];
  rehabStates?: Partial<RehabStateOptions>[];
  updatedAt?: string;
  currentUserAccessLevel?: EpisodeAccessLevel;
  archived?: boolean;
  archivedBy?: Partial<UserOptions> | null;
  archivedByGroup?: Partial<GroupOptions> | null;
  latestLocationEpisodeId?: string;
  latestLocationEpisode?: Partial<LocationEpisodeOptions>;
  locationEpisodes: Partial<LocationEpisodeSummaryOptions>[];
  episodeId?: string;
  externalId?: string | null;
  latest?: boolean;
  trackLos?: boolean;
  hasNewChanges?: boolean;
  owner?: Partial<GroupOptions>;
  unacknowledgedEscalations?: number;
  unacknowledgedPriorityNotes?: number;
  removedFromCensus?: boolean;
  statusOutOfDate?: boolean;
  episodeGroups?: Partial<EpisodeGroupOptions>[];
  latestLocationEpisodeDischarge?: Partial<LocationEpisodeDischargeOptions> | null;
  transferredFrom?: Partial<GroupOptions>;
}

function getDefaults(): EpisodeOptions {
  return {
    id: '',
    payor: null,
    medicare: null,
    admittedOn: '',
    currentRehabState: {} as RehabStateOptions,
    latestLocationEpisodeId: '',
    latestLocationEpisode: {} as LocationEpisodeOptions,
    locationEpisodes: [],
    owner: {},
    patient: {},
    physicianTeam: {},
    affiliates: [],
    rehabStates: [
      {
        ...RehabStates.Queue,
        current: true,
        position: 0,
      } as RehabStateOptions,
    ],
    updatedAt: '',
    archived: false,
    archivedBy: null,
    archivedByGroup: null,
    episodeGroups: [],
    latestLocationEpisodeDischarge: null,
  };
}
/**
 * Episode model
 * @param options EpisodeOptions
 * @property id string
 * @property payor string | null
 * @property medicare boolean | null
 * @property admittedOn string | Date
 * @property currentRehabState RehabState
 * @property patient Patient
 * @property {AttrValue} physicianTeam
 * @property {Group[]} affiliates
 * @property rehabStates RehabState[]
 * @property updatedAt string
 * @property archived boolean
 * @property {User | null} archivedBy
 * @property {GroupOptions | null} archivedByGroup
 * @property latestLocationEpisodeId string
 * @property locationEpisodes LocationEpisode[]
 * @property episodeId string
 * @property externalId string | null
 * @property latest boolean
 * @property trackLos boolean
 * @property hasNewChanges boolean
 * @property owner Group
 * @property unacknowledgedEscalations number
 * @property unacknowledgedPriorityNotes number
 * @property removedFromCensus boolean
 * @property statusOutOfDate boolean
 * @property {EpisodeGroup[]} episodeGroups
 * @property {LocationEpisodeDischarge | null} latestLocationEpisodeDischarge
 * @returns Episode
 * @example const episode = new Episode({ id: '123' });
 */
export default class Episode {
  id: string;
  payor: string | null;
  medicare: boolean | null;
  admittedOn: string | Date;
  currentRehabState: RehabState;
  patient: Patient;
  physicianTeam: AttrValue;
  affiliates: Group[];
  rehabStates?: RehabState[];
  currentUserAccessLevel?: EpisodeAccessLevel;
  updatedAt?: string;
  archived?: boolean;
  archivedBy: User | null;
  archivedByGroup: Group | null;
  latestLocationEpisodeId?: string;
  latestLocationEpisode?: LocationEpisode;
  locationEpisodes: LocationEpisodeSummary[];
  episodeId?: string;
  externalId?: string | null;
  latest?: boolean;
  trackLos?: boolean;
  hasNewChanges?: boolean;
  owner: Group;
  unacknowledgedEscalations?: number;
  unacknowledgedPriorityNotes?: number;
  removedFromCensus?: boolean;
  statusOutOfDate?: boolean;
  episodeGroups?: EpisodeGroup[];
  latestLocationEpisodeDischarge?: LocationEpisodeDischarge | null;
  transferredFrom?: Group | null;

  constructor(options: Partial<EpisodeOptions> = {}) {
    const opts = { ...getDefaults(), ...options };

    this.id = opts.id;
    this.payor = opts.payor;
    this.admittedOn = opts.admittedOn;
    this.currentRehabState = new RehabState(opts.currentRehabState);
    this.physicianTeam = new AttrValue(opts.physicianTeam);
    this.affiliates = opts.affiliates?.map((x) => new Group(x));
    this.rehabStates = opts.rehabStates?.map((x) => new RehabState(x));
    this.updatedAt = opts.updatedAt;
    this.currentUserAccessLevel = opts.currentUserAccessLevel;
    this.archived = opts.archived;
    this.archivedBy = opts.archivedBy ? new User(opts.archivedBy) : null;
    this.archivedByGroup = opts.archivedByGroup ? new Group(opts.archivedByGroup) : null;
    this.latestLocationEpisodeId = opts.latestLocationEpisodeId;
    this.latestLocationEpisode = new LocationEpisode(opts.latestLocationEpisode);
    this.locationEpisodes = opts.locationEpisodes.map((x) => new LocationEpisodeSummary(x));
    this.episodeId = opts.episodeId;
    this.externalId = opts.externalId;
    this.latest = opts.latest;
    this.trackLos = opts.trackLos;
    this.hasNewChanges = opts.hasNewChanges;
    this.owner = new Group(opts.owner);
    this.unacknowledgedEscalations = opts.unacknowledgedEscalations;
    this.unacknowledgedPriorityNotes = opts.unacknowledgedPriorityNotes;
    this.removedFromCensus = opts.removedFromCensus;
    this.statusOutOfDate = opts.statusOutOfDate;
    this.episodeGroups = opts.episodeGroups?.map(
      (x) => new EpisodeGroup({ ...x, isOwner: x.groupId === this.owner?.id })
    );
    this.patient = new Patient({ ...opts.patient, episodeGroups: this.episodeGroups });
    this.latestLocationEpisodeDischarge = opts.latestLocationEpisodeDischarge
      ? new LocationEpisodeDischarge(opts.latestLocationEpisodeDischarge)
      : null;
    this.transferredFrom = opts.transferredFrom ? new Group(opts.transferredFrom) : null;
  }

  get type() {
    return 'Episode';
  }

  get orderedActiveLocationEpisodes() {
    return this.locationEpisodes
      .filter((le) => !le.archived)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }
}
